/**
 * License & Investor — sales-ready landing page for licensing or equity deals.
 *  - 3 license tiers + equity/acquisition card
 *  - Investor demo embed
 *  - Lead capture form → POST /api/leads
 *  - "Press Kit" download = print-to-PDF of this page
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Check, Mail, Printer, X, Play } from "lucide-react";
import { http } from "@/lib/http";
import { toast } from "sonner";

const TIERS = [
  {
    key: "license_single",
    name: "Single Venue License",
    price: "$75K",
    cadence: "one-time + 8% rev share",
    headline: "Light up one stage in your city.",
    perks: [
      "Full white-label of the Hologram Club platform",
      "All 4 Black Original Artists (KAIRO, ZADE, ONYX, NUBIA)",
      "Unlimited tribute holograms (Elvis, MJ, Whitney, 2Pac, Prince…)",
      "Stripe + membership tiers + PPV billing baked in",
      "12-month exclusivity in your metro",
    ],
    cta: "Reserve A City",
  },
  {
    key: "license_regional",
    name: "Regional Master",
    price: "$350K",
    cadence: "+ 12% rev share, 3-venue pack",
    headline: "Own a region — open 3 venues in 18 months.",
    perks: [
      "Everything in Single Venue × 3",
      "Custom AI hologram generator for your local artists",
      "Co-marketing with the Hologram Club brand",
      "Direct line to engineering for venue-specific features",
      "Right of first refusal on adjacent metros",
    ],
    cta: "Claim A Region",
    featured: true,
  },
  {
    key: "license_master",
    name: "National Master",
    price: "$1.5M+",
    cadence: "+ 15% rev share, full territory",
    headline: "The whole country. Or pick a continent.",
    perks: [
      "Exclusive North America (or other continent)",
      "Sublicense rights — flip individual venues yourself",
      "Joint venture on the next 4 originals (you co-own IP)",
      "Annual roadmap sessions + voting rights on platform features",
      "First option on Hologram Club acquisition",
    ],
    cta: "Take The Country",
  },
];

const INTERESTS = [
  { v: "license_single", l: "Single Venue License" },
  { v: "license_regional", l: "Regional Master" },
  { v: "license_master", l: "National Master" },
  { v: "equity", l: "Equity Investment" },
  { v: "acquisition", l: "Outright Acquisition" },
  { v: "other", l: "Other / Just Curious" },
];

export default function License() {
  const [open, setOpen] = useState(false);
  const [presetInterest, setPresetInterest] = useState("equity");

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="license-page">
      {/* Top nav */}
      <nav className="fixed top-0 inset-x-0 z-30 px-5 md:px-10 py-5 flex items-center justify-between border-b border-white/8 bg-black/65 backdrop-blur">
        <Link to="/hologram-club" className="font-display text-2xl font-black tracking-tighter">
          HOLOGRAM<span className="text-[#FF007F]">·</span>CLUB
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/hologram-club/demo" className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FFD166] hover:text-white" data-testid="license-watch-demo">
            <Play size={11} className="inline mr-1.5" /> Watch Demo
          </Link>
          <button
            onClick={() => { setPresetInterest("equity"); setOpen(true); }}
            data-testid="license-talk-cta"
            className="bg-[#FFD166] hover:bg-white text-black font-mono-ui text-[10px] tracking-[0.32em] uppercase font-bold py-2.5 px-4"
          >
            Let's Talk
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-40 pb-24 px-6 md:px-12 max-w-6xl mx-auto">
        <div className="font-mono-ui text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#FFD166] inline-flex items-center gap-2.5">
          <span className="ring-dot" style={{ width: 6, height: 6, background: "#FFD166", boxShadow: "0 0 10px #FFD166" }} />
          Now Licensing · 2026 Season
        </div>
        <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.85] text-5xl sm:text-7xl md:text-8xl lg:text-[140px]">
          Open a hologram club.
          <br />
          <span className="text-transparent" style={{ WebkitTextStroke: "1.5px #FF007F" }}>
            Print money 24/7.
          </span>
        </h1>
        <p className="mt-8 max-w-3xl text-base md:text-xl text-white/70 leading-relaxed">
          Two flagship venues in Las Vegas + Los Angeles. <strong className="text-white">100% original Black IP</strong> we own forever, plus iconic tribute holograms. Live 24 hours a day. Full Stripe billing, PPV monetisation, and membership tiers ship with every license. Investors can take equity, a venue license, or the whole company. Pick a lane.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <button
            onClick={() => { setPresetInterest("equity"); setOpen(true); }}
            data-testid="license-hero-invest"
            className="inline-flex items-center gap-3 bg-[#FFD166] hover:bg-white text-black font-mono-ui text-[12px] tracking-[0.32em] uppercase font-bold py-4 px-7"
          >
            <Sparkles size={14} /> Invest · Raising $750K — $2M
          </button>
          <Link
            to="/hologram-club/demo"
            data-testid="license-hero-demo"
            className="inline-flex items-center gap-3 border border-white/30 hover:bg-white hover:text-black font-mono-ui text-[12px] tracking-[0.32em] uppercase font-bold py-4 px-7"
          >
            <Play size={14} /> Watch The 5-Minute Reel
          </Link>
        </div>
      </section>

      {/* Numbers */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "20", v: "Hologram Acts" },
            { k: "24/7", v: "Open · Vegas + LA" },
            { k: "$8M", v: "Pre-Money Valuation" },
            { k: "$4M", v: "Projected Annual / Venue" },
          ].map((m) => (
            <div key={m.v} data-testid={`license-stat-${m.v}`}>
              <div className="font-display font-black tracking-tighter text-4xl md:text-6xl text-[#FFD166]">{m.k}</div>
              <div className="font-mono-ui text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-white/55 mt-2">{m.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* License tiers */}
      <section id="license-tiers" className="px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#00E5FF]">License The Platform</div>
        <h2 className="mt-3 font-display font-black tracking-tighter text-4xl md:text-6xl">
          Pick a territory. We'll wire your venue inside 90 days.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIERS.map((t) => (
            <div
              key={t.key}
              data-testid={`license-tier-${t.key}`}
              className={`p-7 border ${t.featured ? "border-[#FFD166] bg-[#FFD166]/5" : "border-white/15 bg-white/[0.02]"} flex flex-col`}
            >
              {t.featured && (
                <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-black bg-[#FFD166] inline-block px-2 py-1 self-start mb-3">
                  Most Popular
                </div>
              )}
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#FFD166]">{t.name}</div>
              <div className="mt-3 font-display font-black tracking-tighter text-5xl">{t.price}</div>
              <div className="mt-1 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/45">{t.cadence}</div>
              <div className="mt-4 text-white/80">{t.headline}</div>
              <ul className="mt-5 space-y-2.5 text-sm text-white/70 flex-1">
                {t.perks.map((p) => (
                  <li key={p} className="flex gap-2.5">
                    <Check size={14} className="text-[#00E5FF] mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { setPresetInterest(t.key); setOpen(true); }}
                data-testid={`license-tier-cta-${t.key}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 font-mono-ui text-[11px] tracking-[0.32em] uppercase font-bold py-3.5 px-5 transition-colors ${t.featured ? "bg-[#FFD166] text-black hover:bg-white" : "border border-white/30 hover:bg-white hover:text-black"}`}
              >
                {t.cta} <ArrowRight size={13} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Equity card */}
      <section className="px-6 md:px-12 py-12 max-w-6xl mx-auto">
        <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-8 md:p-12">
          <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#00E5FF]">Equity Round · Open</div>
          <h2 className="mt-3 font-display font-black tracking-tighter text-3xl md:text-5xl">
            Or own a piece of the whole thing.
          </h2>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            <div>
              <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/45">Pre-Money</div>
              <div className="font-display font-black text-4xl mt-1">$8M</div>
            </div>
            <div>
              <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/45">Raising</div>
              <div className="font-display font-black text-4xl mt-1">$750K — $2M</div>
            </div>
            <div>
              <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/45">For</div>
              <div className="font-display font-black text-4xl mt-1">9% — 25%</div>
            </div>
          </div>
          <button
            onClick={() => { setPresetInterest("equity"); setOpen(true); }}
            data-testid="license-equity-cta"
            className="mt-8 inline-flex items-center gap-3 bg-[#00E5FF] hover:bg-white text-black font-mono-ui text-[11px] tracking-[0.32em] uppercase font-bold py-4 px-7"
          >
            <Mail size={13} /> Send Term Sheet
          </button>
        </div>
      </section>

      {/* Press / print */}
      <section className="px-6 md:px-12 py-12 max-w-6xl mx-auto border-t border-white/8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#FFD166]">Press Kit</div>
            <div className="mt-2 text-white/70 text-sm max-w-2xl">
              Print this page to PDF (Cmd/Ctrl + P → Save as PDF). Becomes a 4-page leave-behind for any meeting.
            </div>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="license-print"
            className="inline-flex items-center gap-2 border border-white/30 hover:bg-white hover:text-black font-mono-ui text-[11px] tracking-[0.32em] uppercase font-bold py-3.5 px-5"
          >
            <Printer size={13} /> Print / Save PDF
          </button>
        </div>
      </section>

      <footer className="px-6 md:px-12 py-12 max-w-6xl mx-auto border-t border-white/8 text-white/45 text-sm flex justify-between">
        <div>© 2026 Hologram Club · All originals 100% owned IP</div>
        <Link to="/hologram-club" className="hover:text-white">Back to club ↗</Link>
      </footer>

      {open && (
        <LeadModal interest={presetInterest} onClose={() => setOpen(false)} />
      )}

      <style>{`
        @media print {
          nav, button, .ring-dot { display: none !important; }
          body { background: #fff !important; color: #000 !important; }
          * { color: #000 !important; background: transparent !important; border-color: #aaa !important; }
        }
      `}</style>
    </div>
  );
}

function LeadModal({ interest, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    org: "",
    interest,
    check_size: "",
    venue_city: "",
    message: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await http.post("/leads", form);
      toast.success("Sent. We'll be in touch within 24 hrs.");
      onClose();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-5" data-testid="lead-modal">
      <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-black" data-testid="lead-modal-close">
        <X size={16} />
      </button>
      <form onSubmit={submit} className="w-full max-w-lg bg-[#0a0a0a] border border-white/15 p-8">
        <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#FFD166]">
          Step Inside
        </div>
        <h3 className="mt-2 font-display font-black tracking-tighter text-3xl">Let's run the numbers.</h3>
        <p className="mt-2 text-sm text-white/60">No spam. We reply within 24 hours, every time.</p>
        <div className="mt-5 space-y-3">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full bg-[#050505] border border-white/15 p-3 font-mono-ui text-sm focus:border-[#FFD166] focus:outline-none" data-testid="lead-name" />
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-[#050505] border border-white/15 p-3 font-mono-ui text-sm focus:border-[#FFD166] focus:outline-none" data-testid="lead-email" />
          <input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Company / Fund (optional)" className="w-full bg-[#050505] border border-white/15 p-3 font-mono-ui text-sm focus:border-[#FFD166] focus:outline-none" data-testid="lead-org" />
          <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="w-full bg-[#050505] border border-white/15 p-3 font-mono-ui text-sm focus:border-[#FFD166] focus:outline-none" data-testid="lead-interest">
            {INTERESTS.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
          </select>
          {form.interest.startsWith("license_") && (
            <input value={form.venue_city} onChange={(e) => setForm({ ...form, venue_city: e.target.value })} placeholder="Target city (e.g. Atlanta)" className="w-full bg-[#050505] border border-white/15 p-3 font-mono-ui text-sm focus:border-[#FFD166] focus:outline-none" data-testid="lead-city" />
          )}
          {(form.interest === "equity" || form.interest === "acquisition") && (
            <input value={form.check_size} onChange={(e) => setForm({ ...form, check_size: e.target.value })} placeholder="Check size (e.g. $250K)" className="w-full bg-[#050505] border border-white/15 p-3 font-mono-ui text-sm focus:border-[#FFD166] focus:outline-none" data-testid="lead-check" />
          )}
          <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything we should know? (optional)" className="w-full bg-[#050505] border border-white/15 p-3 font-mono-ui text-sm focus:border-[#FFD166] focus:outline-none" data-testid="lead-message" />
        </div>
        <button type="submit" disabled={submitting} className="mt-5 w-full bg-[#FFD166] text-black hover:bg-white font-mono-ui text-[11px] tracking-[0.32em] uppercase font-bold py-3.5 disabled:opacity-60" data-testid="lead-submit">
          {submitting ? "Sending..." : "Send · We'll reply in 24 hrs"}
        </button>
      </form>
    </div>
  );
}
